import axios from 'axios'
import { useAgentStore } from '../store/useAgentStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor — attach JWT
apiClient.interceptors.request.use(
  (config) => {
    const token = useAgentStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor — handle 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const refreshToken = localStorage.getItem('refresh_token')
        const response = await axios.post('/api/auth/refresh', {
          refresh_token: refreshToken,
        })
        const { access_token } = response.data
        localStorage.setItem('access_token', access_token)
        // Retry original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed — force logout
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Helper hooks for React Query
export const fetcher = async <T>(url: string): Promise<T> => {
  const response = await apiClient.get<T>(url)
  return response.data
}

export const poster = async <T>(url: string, data: unknown): Promise<T> => {
  const response = await apiClient.post<T>(url, data)
  return response.data
}